/**
 * @file 四川麻将（血战到底）核心领域类型。
 *
 * 设计约束：
 * 1. **平台无关** —— 本文件（及整个 `packages/core/src`）不得引用任何 DOM / Node / Workers 全局。
 *    编译期护栏见 `packages/core/tsconfig.json` 的 `"types": []` 与 `"lib": ["ES2022"]`。
 * 2. **不可变** —— 所有状态结构一律 `readonly`。引擎以「旧状态 → 新状态」纯函数推进，
 *    禁止原地修改；这是单机模式与服务端复用同一份 core 的前提。
 * 3. **实体守恒** —— 每张牌都有唯一 `TileId`（0..107）。任意时刻
 *    `牌墙 ∪ 四家手牌 ∪ 四家副露 ∪ 四家弃牌 ∪ 胡牌区` 必须恰好等于 108 个互不相同的 id。
 *    该不变量由 M1-T4 的属性测试（property test）在每一步 action 后断言。
 */

/* ─────────────────────────── 牌 ─────────────────────────── */

/**
 * 花色。四川麻将只用「万 / 筒 / 条」三门，**无字牌、无花牌、不能吃**。
 */
export type Suit = 'wan' | 'tong' | 'tiao';

/** 花色枚举（渲染与遍历用，顺序即为标准排序顺序）。 */
export const SUITS = ['wan', 'tong', 'tiao'] as const satisfies readonly Suit[];

/** 点数 1..9。 */
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** 点数枚举。 */
export const RANKS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const satisfies readonly Rank[];

/**
 * 牌面种类，共 27 种（3 花色 × 9 点数）。例：`'wan1'`、`'tiao9'`。
 * 这是「牌长什么样」的身份，与具体实例无关。
 */
export type TileKind = `${Suit}${Rank}`;

/**
 * 牌的实例 ID，取值范围 `0 <= id < 108`。
 *
 * **重要契约**：id → 牌面（`TileKind`）的映射是**确定且公开**的
 * （`kind = kindOfTileId(id)`，见 `index.ts`）。因此：
 * - 客户端拿到一个 id 就等于知道了那张牌是什么；
 * - 洗牌洗的是**牌墙数组里 id 的排列顺序**，而不是 id ↔ 牌面的对应关系；
 * - 服务端的信息隔离必须以「该座位是否有权看见这张牌」为唯一过滤维度，
 *   绝不能寄望于「id 看起来像个不透明句柄」。详见 `docs/architecture/00-overview.md` §6。
 *
 * 同一 `TileKind` 对应 4 个连续 id（`kindIndex * 4 + copy`），便于做实体守恒校验。
 */
export type TileId = number;

/** 一副牌的总张数：27 种 × 4 张。 */
export const TOTAL_TILES = 108 as const;

/** 牌面种类总数。 */
export const TILE_KIND_COUNT = 27 as const;

/**
 * 牌实例。`suit` / `rank` 均可由 `id` 推导，此结构仅用于需要富信息的场合
 * （如 AI 评估、UI 渲染）；协议层与状态层一律只传 `TileId` 以压缩载荷。
 */
export interface Tile {
  readonly id: TileId;
  readonly suit: Suit;
  readonly rank: Rank;
  readonly kind: TileKind;
}

/* ─────────────────────────── 座位 ─────────────────────────── */

/** 座位索引，固定四人。0 为房主席位，逆时针递增（0→1→2→3→0）。 */
export type SeatIndex = 0 | 1 | 2 | 3;

/** 座位枚举。 */
export const SEATS = [0, 1, 2, 3] as const satisfies readonly SeatIndex[];

/** 玩家标识。联机为服务端下发的匿名 ID；单机 AI 形如 `'bot:1'`。 */
export type PlayerId = string;

/* ─────────────────────────── 副露（吃碰杠） ─────────────────────────── */

/**
 * 副露类型。四川麻将**没有吃**，故不含 `chi`。
 * - `peng`     碰
 * - `gang_ming` 明杠 / 直杠：他人打出的牌 + 手中三张
 * - `gang_an`   暗杠：手中四张
 * - `gang_bu`   补杠 / 巴杠：已碰的刻子 + 自摸到的第四张（可被抢杠胡）
 */
export type MeldType = 'peng' | 'gang_ming' | 'gang_an' | 'gang_bu';

/** 一组副露。 */
export interface Meld {
  readonly type: MeldType;
  /** 该副露的牌面种类。 */
  readonly kind: TileKind;
  /** 构成该副露的牌实例 id（碰 3 张、杠 4 张），升序。 */
  readonly tiles: readonly TileId[];
  /** 被碰/杠的那张牌来自哪个座位；暗杠与补杠的第四张为自摸时为 `null`。 */
  readonly fromSeat: SeatIndex | null;
  /** 触发该副露的那张牌；暗杠为 `null`。 */
  readonly claimedTileId: TileId | null;
  /** 成立时的全局回合序号，用于「杠上花 / 杠上炮 / 抢杠胡」的时序判定。 */
  readonly atTurn: number;
}

/* ─────────────────────────── 听牌 ─────────────────────────── */

/** 一个听口：听哪张牌、胡了值多少番。 */
export interface TingCandidate {
  readonly kind: TileKind;
  /** 该听口在牌墙 + 他家未打出的牌中理论剩余张数（服务端可算，是否下发由规则配置决定）。 */
  readonly remaining: number;
  /** 胡该张时的预估番数（不含自摸 / 杠上花等临场加成）。 */
  readonly estimatedFan: number;
}

/* ─────────────────────────── 胡牌 ─────────────────────────── */

/** 胡牌方式。 */
export type WinKind =
  /** 自摸 */
  | 'zimo'
  /** 点炮（放炮者 = `fromSeat`） */
  | 'dianpao'
  /** 抢杠胡 */
  | 'qiang_gang'
  /** 杠上花（开杠后摸牌自摸） */
  | 'gang_shang_hua'
  /** 杠上炮（开杠后打出的牌被胡） */
  | 'gang_shang_pao';

/**
 * 一次胡牌记录。血战到底下一局可产生最多 3 条（前三家胡牌后牌局才结束）。
 */
export interface WinRecord {
  readonly seat: SeatIndex;
  readonly kind: WinKind;
  /** 点炮/抢杠的来源座位；自摸为 `null`。 */
  readonly fromSeat: SeatIndex | null;
  readonly winningTile: TileId;
  /** 命中的番型标识列表（具体番型枚举由 design/gdd 规则文档定稿后在 M1-T3 收敛）。 */
  readonly fanTags: readonly string[];
  /** 封顶前的原始番数。 */
  readonly rawFan: number;
  /** 应用封顶后的最终番数。 */
  readonly cappedFan: number;
  /** 胡牌发生时的全局回合序号。 */
  readonly atTurn: number;
}

/* ─────────────────────────── 玩家状态 ─────────────────────────── */

/**
 * 单个座位的完整状态（**server-internal**）。
 * 下发给客户端前必须经 `projectStateForSeat()` 投影脱敏。
 */
export interface PlayerState {
  readonly seat: SeatIndex;
  readonly playerId: PlayerId;
  /** ⚠ 暗手牌。仅本人可见。 */
  readonly hand: readonly TileId[];
  readonly melds: readonly Meld[];
  /** 弃牌河，按打出顺序。公开信息。 */
  readonly discards: readonly TileId[];
  /** 定缺花色；`null` 表示尚未定缺。定缺一旦确定即为公开信息。 */
  readonly voidSuit: Suit | null;
  /** 是否已听牌（叫牌）。⚠ M1 不向他家公开，投影时对非本人座位一律置 `false`。 */
  readonly isTing: boolean;
  /** ⚠ 听口详情。默认仅本人可见。 */
  readonly tingCandidates: readonly TingCandidate[] | null;
  /** 血战到底：已胡牌者不再摸打，但仍可被杠、仍参与最终结算。 */
  readonly hasWon: boolean;
  readonly winRecords: readonly WinRecord[];
  /** 是否为花猪（终局仍持有定缺花色的牌）。结算阶段才有意义。 */
  readonly isFlowerPig: boolean;
  /** 当前累计分数（可为负）。 */
  readonly score: number;
  readonly connected: boolean;
  readonly isRobot: boolean;
}

/* ─────────────────────────── 牌局阶段 ─────────────────────────── */

/**
 * 牌局阶段机。血战到底完整流程：
 * `init → dealing → swap(换三张) → void(定缺) → playing → settlement → finished`
 */
export type GamePhase =
  | 'init'
  | 'dealing'
  | 'swap'
  | 'void'
  | 'playing'
  | 'settlement'
  | 'finished';

/** 换三张的方向（服务端随机决定，四家一致）。 */
export type SwapDirection = 'left' | 'right' | 'across';

/* ─────────────────────────── 待响应（碰杠胡）窗口 ─────────────────────────── */

/**
 * 一个座位可对当前弃牌/杠牌做出的响应候选（**server-internal，极度敏感**）。
 *
 * ⚠ 这个结构直接暴露他人手牌信息：广播「座位 2 可以碰」等价于告诉全场
 * 座位 2 手里有一对刚打出的牌。任何情况下都**只能定向单播给候选者本人**。
 */
export interface ClaimOption {
  readonly seat: SeatIndex;
  readonly actions: readonly ClaimActionType[];
  /** 若可杠，可杠的牌面。 */
  readonly gangKinds: readonly TileKind[];
}

/** 可在响应窗口内发起的动作类型。 */
export type ClaimActionType = 'peng' | 'gang' | 'hu' | 'pass';

/** 一次响应窗口的完整状态（**server-internal**）。 */
export interface PendingClaims {
  /** 触发响应窗口的牌。 */
  readonly tileId: TileId;
  /** 出牌/开杠的座位。 */
  readonly fromSeat: SeatIndex;
  /** 窗口的触发原因：普通打牌 / 补杠（可抢杠胡）。 */
  readonly trigger: 'discard' | 'gang_bu';
  /** ⚠ 候选集，绝不进公开广播。 */
  readonly options: readonly ClaimOption[];
  /** 已作答的座位及其选择。 */
  readonly responses: Readonly<Partial<Record<SeatIndex, ClaimActionType>>>;
  /** 窗口截止时间（epoch ms）。到点由 alarm 自动 pass。 */
  readonly deadlineAt: number;
}

/** 最近一张弃牌（公开信息）。 */
export interface LastDiscard {
  readonly tileId: TileId;
  readonly seat: SeatIndex;
  readonly atTurn: number;
  /** 是否已被人碰/杠/胡走。 */
  readonly claimed: boolean;
}

/* ─────────────────────────── 随机数 ─────────────────────────── */

/**
 * 确定性 PRNG 的可序列化状态（**server-internal，最高敏感级**）。
 *
 * ⚠ 泄漏 `RngState` 等价于泄漏整副牌墙 —— 客户端可用同一算法把后续所有牌推演出来。
 * 它既不能进任何广播事件，也不能写进客户端可读的快照。
 *
 * core 不依赖平台随机源：种子由调用方注入（服务端用 `crypto.getRandomValues`，
 * 单机用本地熵），core 内部只做确定性推进 —— 这保证同一 `seed` + 同一 action 序列
 * 在浏览器与 Workers 上产出**逐位一致**的牌局，从而使回放与对拍测试成为可能。
 */
export interface RngState {
  /** 算法标识，便于日后换算法时保持旧回放可复现。 */
  readonly algo: 'mulberry32';
  /** 初始种子（32 位无符号）。 */
  readonly seed: number;
  /** 已消耗的随机数个数，用于快照恢复。 */
  readonly cursor: number;
}

/* ─────────────────────────── 牌局状态 ─────────────────────────── */

/** 四个座位的定长元组，保证类型层面就是四人局。 */
export type Seats<T> = readonly [T, T, T, T];

/**
 * 完整牌局状态（**server-internal 权威态**）。
 *
 * 这是唯一的事实来源（single source of truth）。客户端永远拿不到它，
 * 只能拿到 `projectStateForSeat()` 产出的 `PublicGameView`（定义在 `@majiang/protocol`）。
 */
export interface GameState {
  readonly gameId: string;
  /**
   * 状态版本号，从 0 开始严格单调递增，每应用一个 action +1。
   * 断线重连的 resync 与事件增量补发都以它为锚点。
   */
  readonly seq: number;
  readonly phase: GamePhase;
  /** 房间创建时固化的规则快照，全局不可变。 */
  readonly rules: RuleSnapshot;
  readonly players: Seats<PlayerState>;
  /** 庄家座位。 */
  readonly dealer: SeatIndex;
  /** 当前应行动的座位。响应窗口开启期间该值仍指向出牌者。 */
  readonly turn: SeatIndex;
  /** ⚠ 牌墙（剩余牌的 id 序列，已洗好）。绝对 server-internal。 */
  readonly wall: readonly TileId[];
  /** ⚠ 牌墙游标。剩余**张数**可公开，牌**序**不可。 */
  readonly wallCursor: number;
  /** 换三张的方向；`swap` 阶段之后固定。 */
  readonly swapDirection: SwapDirection | null;
  readonly lastDiscard: LastDiscard | null;
  /** ⚠ 响应窗口。见 `PendingClaims` 的警告。 */
  readonly pendingClaims: PendingClaims | null;
  /** ⚠ 随机数状态。见 `RngState` 的警告。 */
  readonly rng: RngState;
  /** 全局回合序号，每次摸/打推进。 */
  readonly turnCount: number;
  /** 当前行动的超时截止时间（epoch ms）；`null` 表示无计时。 */
  readonly deadlineAt: number | null;
  /** 已胡牌座位，按胡牌先后顺序。长度达到 3 时牌局进入结算。 */
  readonly winnersInOrder: readonly SeatIndex[];
  /** 终局结算结果；未结算时为 `null`。 */
  readonly settlement: Settlement | null;
}

/* ─────────────────────────── 结算 ─────────────────────────── */

/** 一笔分数转移。 */
export interface ScoreTransfer {
  readonly from: SeatIndex;
  readonly to: SeatIndex;
  readonly amount: number;
  /** 转移原因标识，如 `'zimo'` / `'gang_ming'` / `'cha_dajiao'` / `'cha_huazhu'` / `'hu_jiao_zhuanyi'`。 */
  readonly reason: string;
  /** 人类可读说明，用于结算面板与对局回放。 */
  readonly detail: string;
}

/** 终局结算结果。 */
export interface Settlement {
  readonly transfers: readonly ScoreTransfer[];
  /** 每个座位的最终净分（与 `transfers` 聚合一致，M1-T4 会断言二者自洽）。 */
  readonly netScores: Seats<number>;
  /** 未胡牌但已听牌的座位（查大叫相关）。 */
  readonly dajiaoSeats: readonly SeatIndex[];
  /** 花猪座位（查花猪相关）。 */
  readonly flowerPigSeats: readonly SeatIndex[];
  /** 退税（已收的杠分因未成叫而退回）明细。 */
  readonly gangRefunds: readonly ScoreTransfer[];
}

/* ─────────────────────────── 动作（客户端意图） ─────────────────────────── */

/** 动作类型枚举。 */
export type ActionType =
  | 'declare_void'
  | 'swap_select'
  | 'draw'
  | 'discard'
  | 'peng'
  | 'gang'
  | 'hu'
  | 'pass'
  | 'timeout';

/** 所有动作共有的字段。 */
export interface ActionBase {
  readonly seat: SeatIndex;
  /**
   * 客户端观察到的状态版本号。服务端据此做**乐观并发校验**：
   * 若 `expectedSeq !== state.seq`，该动作因状态已变更而被拒绝。
   */
  readonly expectedSeq: number;
}

/** 定缺：声明一门必须打光的花色。 */
export interface DeclareVoidAction extends ActionBase {
  readonly type: 'declare_void';
  readonly suit: Suit;
}

/** 换三张：选择三张同花色的牌交换出去。 */
export interface SwapSelectAction extends ActionBase {
  readonly type: 'swap_select';
  readonly tiles: readonly [TileId, TileId, TileId];
}

/** 摸牌。通常由引擎自动推进；显式动作用于「手动摸牌」的 UI 模式。 */
export interface DrawAction extends ActionBase {
  readonly type: 'draw';
}

/** 出牌。 */
export interface DiscardAction extends ActionBase {
  readonly type: 'discard';
  readonly tileId: TileId;
}

/** 碰。 */
export interface PengAction extends ActionBase {
  readonly type: 'peng';
  readonly tileId: TileId;
}

/** 杠。`kind` 指明杠哪一门牌，引擎据此推断是明杠 / 暗杠 / 补杠。 */
export interface GangAction extends ActionBase {
  readonly type: 'gang';
  readonly kind: TileKind;
}

/** 胡。 */
export interface HuAction extends ActionBase {
  readonly type: 'hu';
  readonly tileId: TileId;
}

/** 过（放弃本次响应窗口）。 */
export interface PassAction extends ActionBase {
  readonly type: 'pass';
}

/**
 * 超时。**只能由服务端/引擎宿主注入**，客户端发送一律拒绝。
 * 由 Durable Object 的 alarm 或单机端的定时器触发。
 */
export interface TimeoutAction extends ActionBase {
  readonly type: 'timeout';
}

/** 玩家/宿主可提交的动作联合类型。 */
export type Action =
  | DeclareVoidAction
  | SwapSelectAction
  | DrawAction
  | DiscardAction
  | PengAction
  | GangAction
  | HuAction
  | PassAction
  | TimeoutAction;

/* ─────────────────────────── 事件（引擎输出） ─────────────────────────── */

/**
 * 事件可见性范围。这是信息隔离的**执行机制**：
 * 引擎产出的每个事件都自带 `audience`，宿主（DO / 单机 runtime）据此路由，
 * 不需要也不允许在传输层再做二次判断。
 */
export type Audience =
  /** 全房间广播（含旁观者）。载荷必须已脱敏。 */
  | { readonly scope: 'all' }
  /** 仅指定座位可见。 */
  | { readonly scope: 'seat'; readonly seat: SeatIndex }
  /** 除指定座位外的其他人可见（用于「某人摸了一张牌」这类他视角事件）。 */
  | { readonly scope: 'others'; readonly except: SeatIndex };

/** 事件类型枚举。 */
export type GameEventType =
  | 'game_started'
  | 'dealt'
  | 'swap_ready'
  | 'swap_done'
  | 'void_declared'
  | 'void_done'
  | 'drawn'
  | 'discarded'
  | 'claim_window_opened'
  | 'claim_options'
  | 'peng_made'
  | 'gang_made'
  | 'won'
  | 'passed'
  | 'turn_changed'
  | 'ting_changed'
  | 'settled'
  | 'game_finished'
  | 'action_rejected';

/**
 * 引擎输出的领域事件。
 *
 * 契约：`applyAction()` 返回的事件序列是**下发给客户端的唯一渠道**。
 * 宿主不得自行从 `GameState` 里挑字段推送 —— 所有脱敏责任集中在 core，
 * 从而使「服务端权威 + 信息隔离」可以被单元测试完整覆盖（M1-T4 会有一组
 * 「泄漏检测」测试：遍历所有 `scope: 'all'` 事件，断言其载荷不含禁字段）。
 */
export interface GameEvent {
  readonly type: GameEventType;
  /** 产生该事件时的状态版本号，与 `GameState.seq` 对齐。 */
  readonly seq: number;
  readonly audience: Audience;
  /** 事件载荷。具体形状按 `type` 收敛，定义在 `@majiang/protocol`。 */
  readonly payload: Readonly<Record<string, unknown>>;
  /** 服务端时间戳（epoch ms），由宿主注入，便于回放对时。 */
  readonly at: number;
}

/** `applyAction()` 的返回值：新状态 + 待分发事件。 */
export interface ApplyResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

/* ─────────────────────────── 前置声明（避免循环 import） ─────────────────────────── */

import type { RuleSnapshot } from './config';

export type { RuleSnapshot };
